import { useEffect } from 'react';
import { store } from '../redux/configureStore';
import { createMonitoringEvent } from '../redux/reducers/utils/errorLogsReducer';

/**
 * Хук и утилиты глобального трекинга ошибок приложения.
 *
 * Экспортируемые элементы:
 * - `captureError(event, options)` — вручную захватить любое событие/ошибку.
 * - `captureApiError(error, extra)` — адаптер для ошибок axios/HTTP.
 * - `clearErrorLogs()` — очистить локальные логи.
 * - `getErrorLogs()` — получить массив логов из localStorage.
 * - `getErrorTrackingEventName()` — имя CustomEvent, который диспатчится при обновлении логов.
 * - `installGlobalErrorTracking()` — устанавливает window listeners и патчит console, возвращает cleanup().
 * - default `useErrorTracking()` — React hook, который вызывает `installGlobalErrorTracking()` при mount.
 *
 * Замечания по дизайну:
 * - Логи хранятся в localStorage под ключом ERROR_LOG_STORAGE_KEY и ограничиваются MAX_LOGS.
 * - Для уменьшения шума реализована дедупликация событий по fingerprint (map recentFingerprints).
 * - Патчинг консоли происходит единожды и может быть восстановлен при cleanup.
 */

const ERROR_LOG_STORAGE_KEY = 'app:errorLogs';
const ERROR_TRACKING_EVENT = 'app-error-log-updated';
const MAX_LOGS = 500;

let isTrackingInstalled = false;
let consolePatched = false;
const recentFingerprints = new Map();

/* -------------------------------------------------------------------------- */
/* Helper utilities                                                            */
/* -------------------------------------------------------------------------- */

/** Возвращает текущую метку времени в ISO-формате. Используется в payload логов. */
const nowIso = () => new Date().toISOString();

/**
 * Генерирует уникальный идентификатор для записи лога.
 * Сначала пытается использовать `crypto.randomUUID()`, при отсутствии — fallback.
 */
const createId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

/** Безопасная сериализация значения: fallback к String() при циклических ссылках. */
const safeStringify = (value) => {
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
};

/**
 * Рекурсивно очищает значение для логирования:
 * - сокращает глубину рекурсии до 4 уровней,
 * - заменяет функции на строку '[Function]',
 * - обрезает массивы/объекты до разумного размера,
 * - извлекает из Error только имя, сообщение и стек.
 */
const sanitize = (value, depth = 0) => {
  if (value == null) return value;
  if (depth > 4) return '[MaxDepthReached]';
  if (typeof value === 'function') return '[Function]';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return value;
  if (value instanceof Error) {
    return {
      name: value.name,
      message: value.message,
      stack: value.stack,
    };
  }
  if (Array.isArray(value)) return value.slice(0, 30).map((item) => sanitize(item, depth + 1));
  if (typeof value === 'object') {
    const result = {};
    Object.keys(value).slice(0, 50).forEach((key) => {
      result[key] = sanitize(value[key], depth + 1);
    });
    return result;
  }
  return String(value);
};

/** Читает логи из localStorage. При ошибке парсинга возвращает [] (без выброса). */
const readLogs = () => {
  try {
    const raw = localStorage.getItem(ERROR_LOG_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

/** Записывает логи в localStorage, обрезая до MAX_LOGS. Ошибки записи игнорируются. */
const writeLogs = (logs) => {
  try {
    localStorage.setItem(ERROR_LOG_STORAGE_KEY, JSON.stringify(logs.slice(0, MAX_LOGS)));
  } catch {
    // ignore storage failures
  }
};

/** Диспатчит глобальное событие, которое слушает UI-страница логов. */
const emitUpdate = () => {
  window.dispatchEvent(new CustomEvent(ERROR_TRACKING_EVENT));
};

/** Формирует простую «отпечаток» (fingerprint) лога, используемый для дедупликации. */
const makeFingerprint = (log) => {
  return `${log.type || 'unknown'}|${log.level || 'error'}|${log.message || ''}|${log.stack || ''}|${log.request?.url || ''}|${log.request?.method || ''}|${log.request?.status || ''}`;
};

/** Проверяет, следует ли пропустить лог как дубликат.
 * - Использует fingerprint и карту recentFingerprints.
 * - Пропускает события, повторяющиеся чаще, чем каждые 3000 мс.
 * - Поддерживает ограничение размера карты и очистку старых записей.
 */
const shouldSkipAsDuplicate = (log) => {
  const fingerprint = makeFingerprint(log);
  const timestamp = Date.now();
  const lastSeen = recentFingerprints.get(fingerprint);

  if (lastSeen && timestamp - lastSeen < 3000) {
    try {
      console.info('[ErrorTracking] skip duplicate event', {
        message: log?.message,
        type: log?.type,
        level: log?.level,
      });
    } catch {
      // ignore logger issues
    }
    return true;
  }

  recentFingerprints.set(fingerprint, timestamp);

  if (recentFingerprints.size > 300) {
    const entries = [...recentFingerprints.entries()].sort((a, b) => b[1] - a[1]).slice(0, 200);
    recentFingerprints.clear();
    entries.forEach(([key, ts]) => recentFingerprints.set(key, ts));
  }

  return false;
};

/**
 * Решает, нужно ли отправлять лог на сервер.
 * Правила:
 * - Всегда отправляем события с тегом 'api' (ошибки запросов).
 * - Отправляем ошибки уровня 'error' и 'critical'.
 * - Пропускаем 'warning' и ниже.
 */
const shouldSendToServer = (log) => {
  if (!log) return false;
  if (Array.isArray(log.tags) && log.tags.includes('api')) return true;
  if (log.level === 'critical') return true;
  // if (log.level === 'error') return true;
  return false;
};

/**
 * Отправляет отдельное событие на сервер через redux-thunk `createMonitoringEvent`.
 */
function sendMonitoringEvent(log) {
  try {
    if (!shouldSendToServer(log)) return;
    const payload = {
      pageUrl: log.pageUrl,
      userAgent: log.userAgent,
      request: log.request,
      response: log.response,
      message: log.message || '',
      meta: log.meta,
      tags: log.tags,
      stack: log.stack,
      isAuth: log.isAuth,
      token: log.token,
      userPhone: log.userPhone,
      userName: log.userName,
    };

    const eventData = {
      eventUuid: log.id,
      occurredAt: log.timestamp,
      level: log.level || 'error',
      source: log.type || 'console',
      message: log.message || '',
      httpStatus: log.request?.status || log.response?.status || 200,
      httpPath: log.request?.url || '',

      payload: payload,
      status: 'new',
    };
    store.dispatch(createMonitoringEvent(eventData));
  } catch (err) {
    try {
      console.error('[ErrorTracking] sendMonitoringEvent failed', err);
    } catch {}
  }
}

/**
 * Захватывает произвольное событие/ошибку и сохраняет его в локальные логи.
 *
 * @param {Object} event - объект ошибки/события (должен содержать `message`, `stack`, `request`, `response`, `meta`, `tags` и т.д.)
 * @param {Object} [options] - опции: `{ sendRemote: boolean }` (по умолчанию true) — управление отправкой на сервер.
 * @returns {Object} записанный payload (можно использовать для дальнейшей телеметрии).
 */
export const captureError = (event, options = {}) => {
  const isAuth = store?.getState?.()?.auth?.isAuth || '';
  const token = localStorage.getItem('token');
  const userPhone = store?.getState?.()?.auth?.userProfile?.phone || '';
  const userName = store?.getState?.()?.auth?.userProfile?.name || '';

  const payload = {
    id: createId(),
    timestamp: nowIso(),
    level: event?.level || 'error',
    type: event?.type || 'console',
    message: event?.message || 'Unknown error',
    stack: event?.stack || null,
    pageUrl: window.location.href,
    request: sanitize(event?.request || null),
    response: sanitize(event?.response || null),
    meta: sanitize(event?.meta || null),
    tags: Array.isArray(event?.tags) ? event.tags.slice(0, 20) : [],
    isAuth,
    token,
    userPhone,
    userName,
    userAgent: navigator.userAgent,
  };

  if (shouldSkipAsDuplicate(payload)) {
    return payload;
  }

  const current = readLogs();
  const next = [payload, ...current];
  writeLogs(next);
  emitUpdate();

  if (options.sendRemote !== false) {
    sendMonitoringEvent(payload);
  }

  return payload;
};

/**
 * Адаптер для ошибок HTTP/axios.
 * Преобразует axios-ошибку в структуру лога и вызывает `captureError`.
 * `extra` используется для добавления дополнительных полей в `meta`.
 */
export const captureApiError = (error, extra = {}) => {
  const status = error?.response?.status || null;
  const errorMessage = error?.response?.data?.message || error?.message || 'API request failed';

  return captureError({
    type: 'http',
    level: status >= 500 ? 'critical' : 'error',
    message: errorMessage,
    stack: error?.stack,
    request: {
      url: error?.config?.url,
      method: error?.config?.method?.toUpperCase(),
      status,
      data: sanitize(error?.config?.data),
      headers: sanitize(error?.config?.headers),
    },
    response: sanitize(error?.response?.data),
    meta: {
      code: error?.code,
      ...sanitize(extra),
    },
    tags: ['api'],
  });
};

/** Удаляет все логи из localStorage и уведомляет слушателей. */
export const clearErrorLogs = () => {
  localStorage.removeItem(ERROR_LOG_STORAGE_KEY);
  emitUpdate();
};

/** Возвращает все логи из localStorage. */
export const getErrorLogs = () => readLogs();

/** Имя глобального события, которое диспатчится при обновлении логов. */
export const getErrorTrackingEventName = () => ERROR_TRACKING_EVENT;

/**
 * Устанавливает глобальные обработчики ошибок и патчит `console`.
 *
 * Поведение:
 * - Если трекинг уже установлен — возвращает no-op cleanup (идемпотентно).
 * - Добавляет слушатели `window.error` и `unhandledrejection` для автоматического захвата.
 * - Патчит `console.error` и `console.warn` чтобы отдельно логировать сообщения из кода.
 * - Возвращает функцию cleanup(), которая снимает слушатели и восстанавливает консоль.
 */
export const installGlobalErrorTracking = () => {
  if (isTrackingInstalled) {
    return () => undefined;
  }

  isTrackingInstalled = true;

  const onWindowError = (event) => {
    captureError({
      type: 'runtime',
      level: 'critical',
      message: event?.message || event?.error?.message || 'Unhandled window error',
      stack: event?.error?.stack,
      meta: {
        filename: event?.filename,
        lineno: event?.lineno,
        colno: event?.colno,
      },
      tags: ['window-error'],
    });
  };

  const onUnhandledRejection = (event) => {
    const reason = event?.reason;
    captureError({
      type: 'promise',
      level: 'critical',
      message: reason?.message || 'Unhandled promise rejection',
      stack: reason?.stack,
      meta: sanitize(reason),
      tags: ['unhandledrejection'],
    });
  };

  window.addEventListener('error', onWindowError);
  window.addEventListener('unhandledrejection', onUnhandledRejection);

  let restoreConsole = () => undefined;
  if (!consolePatched) {
    consolePatched = true;
    const originalError = console.error;
    const originalWarn = console.warn;

    console.error = (...args) => {
      try {
        captureError({
          type: 'console',
          level: 'error',
          message: args.map((arg) => (typeof arg === 'string' ? arg : safeStringify(sanitize(arg)))).join(' '),
          meta: { args: sanitize(args) },
          tags: ['console'],
        });
      } catch {
        // ignore
      }

      originalError.apply(console, args);
    };

    console.warn = (...args) => {
      try {
        captureError({
          type: 'console',
          level: 'warning',
          message: args.map((arg) => (typeof arg === 'string' ? arg : safeStringify(sanitize(arg)))).join(' '),
          meta: { args: sanitize(args) },
          tags: ['console'],
        });
      } catch {
        // ignore
      }

      originalWarn.apply(console, args);
    };

    restoreConsole = () => {
      console.error = originalError;
      console.warn = originalWarn;
      consolePatched = false;
    };
  }

  return () => {
    window.removeEventListener('error', onWindowError);
    window.removeEventListener('unhandledrejection', onUnhandledRejection);
    restoreConsole();
    isTrackingInstalled = false;
  };
};

/**
 * React hook: при монтировании устанавливает глобальную обработку ошибок.
 * Вызывает `installGlobalErrorTracking()` и выполняет cleanup при unmount.
 */
const useErrorTracking = () => {
  useEffect(() => {
    const cleanup = installGlobalErrorTracking();
    return () => {
      if (typeof cleanup === 'function') {
        cleanup();
      }
    };
  }, []);
};

export default useErrorTracking;
